
import { motion } from 'framer-motion';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, LineChart, PieChart, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend, Bar, Line, Pie, Cell } from 'recharts';
import { Activity, Terminal, Code, Zap, AlertCircle, CheckCircle2 } from 'lucide-react';

// Sample data for charts
const performanceData = [
  { name: 'Jan', value: 65 },
  { name: 'Feb', value: 59 },
  { name: 'Mar', value: 80 },
  { name: 'Apr', value: 81 },
  { name: 'May', value: 56 },
  { name: 'Jun', value: 55 },
  { name: 'Jul', value: 40 },
];

const qualityData = [
  { name: 'Code Coverage', value: 80 },
  { name: 'Technical Debt', value: 12 },
  { name: 'Bugs', value: 8 },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const riskData = [
  { name: 'High', value: 3, color: '#ef4444' },
  { name: 'Medium', value: 7, color: '#f59e0b' },
  { name: 'Low', value: 12, color: '#10b981' },
];

export default function Dashboard() {
  return (
      <DashboardLayout>
        <motion.div
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
        >
          <motion.h1
              className="text-3xl font-semibold"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
          >
            Dashboard
          </motion.h1>

          <motion.p
              className="text-muted-foreground"
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
          >
            Your central command center for monitoring project health and team performance.
          </motion.p>

          <Tabs defaultValue="overview" className="w-full mt-6">
            <TabsList className="grid w-full grid-cols-3 md:w-auto">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="performance">Performance</TabsTrigger>
              <TabsTrigger value="risks">Risks</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {[
                  {
                    title: "SonarQube Score",
                    value: "A+",
                    description: "Code quality rating",
                    icon: Code,
                    trend: "up"
                  },
                  {
                    title: "PageSpeed",
                    value: "96/100",
                    description: "Desktop performance",
                    icon: Zap,
                    trend: "up"
                  },
                  {
                    title: "Active Risks",
                    value: "3",
                    description: "High priority issues",
                    icon: AlertCircle,
                    trend: "down"
                  },
                  {
                    title: "Sprint Delivery",
                    value: "92%",
                    description: "Tasks completed",
                    icon: CheckCircle2,
                    trend: "up"
                  },
                ].map((stat, index) => (
                    <Card key={index} className="overflow-hidden">
                      <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                          {stat.title}
                        </CardTitle>
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <stat.icon className="w-4 h-4 text-primary" />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{stat.value}</div>
                        <p className="text-xs text-muted-foreground">
                          {stat.description}
                        </p>
                      </CardContent>
                    </Card>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="col-span-1">
                  <CardHeader>
                    <CardTitle>Performance Trend</CardTitle>
                    <CardDescription>Last 7 months</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                            data={performanceData}
                            margin={{
                              top: 5,
                              right: 30,
                              left: 20,
                              bottom: 5,
                            }}
                        >
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line
                              type="monotone"
                              dataKey="value"
                              stroke="#8884d8"
                              activeDot={{ r: 8 }}
                              name="Performance Score"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card className="col-span-1">
                  <CardHeader>
                    <CardTitle>Code Quality</CardTitle>
                    <CardDescription>Current distribution</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px] flex items-center justify-center">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                              data={qualityData}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={90}
                              fill="#8884d8"
                              paddingAngle={5}
                              dataKey="value"
                              nameKey="name"
                              label
                          >
                            {qualityData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="performance" className="mt-6">
              <div className="grid grid-cols-1 gap-4">
                <Card>
                  <CardHeader className="flex flex-row items-center">
                    <div>
                      <CardTitle>Team Velocity</CardTitle>
                      <CardDescription>Story points per sprint</CardDescription>
                    </div>
                    <Activity className="ml-auto h-5 w-5 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="h-[400px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={[
                              { sprint: 'Sprint 1', points: 45 },
                              { sprint: 'Sprint 2', points: 52 },
                              { sprint: 'Sprint 3', points: 48 },
                              { sprint: 'Sprint 4', points: 61 },
                              { sprint: 'Sprint 5', points: 58 },
                              { sprint: 'Sprint 6', points: 64 },
                            ]}
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <XAxis dataKey="sprint" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="points" fill="#8884d8" name="Story Points" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Cycle Time Analysis</CardTitle>
                    <CardDescription>Average days from start to completion</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                            data={[
                              { month: 'Jan', frontend: 3.2, backend: 4.1, design: 2.5 },
                              { month: 'Feb', frontend: 3.1, backend: 3.8, design: 2.3 },
                              { month: 'Mar', frontend: 2.9, backend: 3.5, design: 2.2 },
                              { month: 'Apr', frontend: 2.8, backend: 3.2, design: 2.0 },
                              { month: 'May', frontend: 2.6, backend: 3.0, design: 1.8 },
                            ]}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="frontend" stroke="#8884d8" name="Frontend" />
                          <Line type="monotone" dataKey="backend" stroke="#82ca9d" name="Backend" />
                          <Line type="monotone" dataKey="design" stroke="#ffc658" name="Design" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="risks" className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Risk Distribution</CardTitle>
                    <CardDescription>By priority level</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                              data={riskData}
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                              nameKey="name"
                              label
                          >
                            {riskData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card className="col-span-2">
                  <CardHeader>
                    <CardTitle>Top Risks</CardTitle>
                    <CardDescription>Highest priority issues</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-4">
                      {[
                        { title: "API Performance", description: "Backend response time exceeding SLA", priority: "High", owner: "Backend Team" },
                        { title: "Test Coverage", description: "Unit tests below 80% threshold", priority: "Medium", owner: "QA Team" },
                        { title: "UX Improvements", description: "User feedback indicates navigation issues", priority: "Medium", owner: "Design Team" },
                      ].map((risk, index) => (
                          <li key={index} className="flex items-start gap-4 p-3 border rounded-lg">
                            <div className={`mt-1 w-3 h-3 rounded-full ${
                                risk.priority === 'High' ? 'bg-red-500' :
                                    risk.priority === 'Medium' ? 'bg-amber-500' : 'bg-green-500'
                            }`} />
                            <div>
                              <h4 className="font-medium">{risk.title}</h4>
                              <p className="text-sm text-muted-foreground">{risk.description}</p>
                              <div className="flex gap-2 mt-2">
                                <span className="text-xs bg-muted px-2 py-1 rounded-md">{risk.priority}</span>
                                <span className="text-xs bg-muted px-2 py-1 rounded-md">{risk.owner}</span>
                              </div>
                            </div>
                          </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Risk Trend</CardTitle>
                  <CardDescription>Number of active risks over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                          data={[
                            { month: 'Jan', high: 5, medium: 8, low: 10 },
                            { month: 'Feb', high: 4, medium: 9, low: 11 },
                            { month: 'Mar', high: 3, medium: 8, low: 13 },
                            { month: 'Apr', high: 4, medium: 7, low: 12 },
                            { month: 'May', high: 3, medium: 7, low: 12 },
                          ]}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="high" stroke="#ef4444" name="High Priority" />
                        <Line type="monotone" dataKey="medium" stroke="#f59e0b" name="Medium Priority" />
                        <Line type="monotone" dataKey="low" stroke="#10b981" name="Low Priority" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      </DashboardLayout>
  );
}
